import { Component } from 'react';

/**
 * Patch google.maps.Map constructor to inject colorScheme at creation time.
 * react-google-maps v1 calls `new google.maps.Map(node)` with NO options,
 * then applies options via setOptions() -- but colorScheme can only be set
 * during construction. This wrapper intercepts the constructor to inject it.
 *
 * MUST be called after importLibrary resolves but BEFORE any Map component
 * mounts, because withGoogleMap creates the map instance in componentDidMount.
 */
var _originalMapConstructor = null;

function patchMapConstructor() {
  if (!window.google || !window.google.maps || !window.google.maps.Map) return;
  if (_originalMapConstructor) return;

  _originalMapConstructor = window.google.maps.Map;
  var OrigMap = _originalMapConstructor;

  function PatchedMap(node, opts) {
    var isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    var ColorScheme = window.google.maps.ColorScheme;
    var colorScheme = ColorScheme
      ? (isDark ? ColorScheme.DARK : ColorScheme.LIGHT)
      : (isDark ? 'DARK' : 'LIGHT');

    var patchedOpts = {};
    if (opts) {
      for (var k in opts) {
        if (opts.hasOwnProperty(k)) {
          patchedOpts[k] = opts[k];
        }
      }
    }
    patchedOpts.colorScheme = colorScheme;
    return new OrigMap(node, patchedOpts);
  }

  PatchedMap.prototype = OrigMap.prototype;

  // Copy static properties
  try {
    var names = Object.getOwnPropertyNames(OrigMap);
    for (var i = 0; i < names.length; i++) {
      var name = names[i];
      if (name !== 'prototype' && name !== 'length' && name !== 'name' && name !== 'caller' && name !== 'arguments') {
        try {
          var desc = Object.getOwnPropertyDescriptor(OrigMap, name);
          if (desc) {
            Object.defineProperty(PatchedMap, name, desc);
          }
        } catch (e) {
          // Some properties may not be configurable
        }
      }
    }
  } catch (e) {
    // Fallback if getOwnPropertyNames not supported
  }

  window.google.maps.Map = PatchedMap;
}

/**
 * Shared, cached promise that loads all Google Maps libraries via
 * importLibrary() and patches the Map constructor. Exported so code that
 * needs google.maps classes BEFORE a GoogleMapsLoader-wrapped component
 * mounts (e.g. MapView geocoding) can wait on the same load.
 */
var _librariesPromise = null;

export function loadGoogleMapsLibraries() {
  if (_librariesPromise) {
    return _librariesPromise;
  }

  if (window.google && window.google.maps && window.google.maps.importLibrary) {
    _librariesPromise = Promise.all([
      window.google.maps.importLibrary('maps'),
      window.google.maps.importLibrary('core'),
      window.google.maps.importLibrary('geocoding'),
      window.google.maps.importLibrary('places'),
      window.google.maps.importLibrary('geometry'),
      window.google.maps.importLibrary('drawing'),
      window.google.maps.importLibrary('marker'),
    ]).then(function () {
      window.googleMapsLoaded = true;
      patchMapConstructor();
    }).catch(function (err) {
      // Allow a retry on the next call instead of caching the failure
      _librariesPromise = null;
      throw err;
    });
  } else if (window.googleMapsLoaded) {
    // Legacy script tag already loaded everything
    _librariesPromise = Promise.resolve().then(function () {
      patchMapConstructor();
    });
  } else {
    // Legacy path: wait for the script tag callback
    _librariesPromise = new Promise(function (resolve) {
      window.onGoogleMapsLoaded = function () {
        patchMapConstructor();
        resolve();
      };
    });
  }

  return _librariesPromise;
}

/**
 * Higher-order component that waits for Google Maps API to load
 * before rendering the wrapped component.
 *
 * Uses importLibrary() to load all required libraries, which enables
 * colorScheme support for dark mode.
 */
class GoogleMapsLoader extends Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.state = {
      isLoaded: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    var self = this;

    loadGoogleMapsLibraries()
      .then(function () {
        if (self._isMounted) {
          self.setState({ isLoaded: true });
        }
      })
      .catch(function (err) {
        console.error('Failed to load Google Maps libraries:', err);
      });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    var isLoaded = this.state.isLoaded;
    var children = this.props.children;
    var loadingComponent = this.props.loadingComponent;

    if (!isLoaded) {
      return loadingComponent || null;
    }

    return children;
  }
}

export default GoogleMapsLoader;
